/**
 * HTTP client for tenant distribution templates (base /api/v2/distribution-templates).
 * Writes answer 202; poll `getTemplateEdit` (a proposed route, see the handoff doc) until done.
 * Writes send an `Idempotency-Key`, and `?story_id=` when the builder was opened from a story.
 * An uploaded PowerPoint template is written with `source: "pptx_master"` and the upload's
 * `master_upload_id` (the `master_id` from distributionMastersApi), sent as given.
 */
import { API_ENDPOINTS, apiService } from "../api";
import { newIdempotencyKey, writeOptions } from "./distributionIdempotency";
import { isMockDistributionsApi } from "./distributionsApi";
import * as mock from "./distributionsApi.mock";
import type {
  EditStatusApi,
  TemplateApi,
  TemplateListApi,
  TemplateWriteAcceptedApi,
  TemplateWriteApi,
} from "./distributions.types";

const E = API_ENDPOINTS.distributionTemplates;

/**
 * Round 7 (pd7 §2.4): an uploaded PowerPoint template's read carries its upload (`master_id`) and the
 * design read from it (`look`). Older answers have neither; every read item has both, null unless set.
 */
const withMasterFields = (template: TemplateApi): TemplateApi => ({
  ...template,
  master_id: template.master_id ?? null,
  look: template.look ?? null,
});

export const listDistributionTemplates = async (): Promise<TemplateListApi> => {
  const list = isMockDistributionsApi() ? await mock.mockListTemplates() : await apiService.get<TemplateListApi>(E.list);
  return { ...list, items: (list.items ?? []).map(withMasterFields) };
};

/** The story the template builder was opened from, if any, and the write's idempotency key. */
export interface TemplateWriteContext {
  storyId?: string | null;
  idempotencyKey?: string;
}

const templateWriteOptions = ({ storyId, idempotencyKey = newIdempotencyKey() }: TemplateWriteContext) =>
  writeOptions(idempotencyKey, storyId ? { story_id: storyId } : undefined);

export const createDistributionTemplate = (body: TemplateWriteApi, context: TemplateWriteContext = {}): Promise<TemplateWriteAcceptedApi> =>
  isMockDistributionsApi() ? mock.mockCreateTemplate(body) : apiService.post<TemplateWriteAcceptedApi>(E.list, body, templateWriteOptions(context));

export const updateDistributionTemplate = (templateId: string, body: TemplateWriteApi, context: TemplateWriteContext = {}): Promise<TemplateWriteAcceptedApi> =>
  isMockDistributionsApi()
    ? mock.mockUpdateTemplate(templateId, body)
    : apiService.patch<TemplateWriteAcceptedApi>(E.detail(templateId), body, templateWriteOptions(context));

/** The delete route takes no `story_id`; only the key is sent. */
export const deleteDistributionTemplate = (templateId: string, idempotencyKey = newIdempotencyKey()): Promise<TemplateWriteAcceptedApi> =>
  isMockDistributionsApi()
    ? mock.mockDeleteTemplate(templateId)
    : apiService.delete<TemplateWriteAcceptedApi>(E.detail(templateId), writeOptions(idempotencyKey));

export const getTemplateEdit = (requestId: string): Promise<EditStatusApi> =>
  isMockDistributionsApi() ? mock.mockGetTemplateEdit(requestId) : apiService.get<EditStatusApi>(E.edit(requestId));
